/**
 * Frontend Error Handling Utility
 * Provides user-friendly error messages in Malay
 */

/**
 * Error code to Malay message mapping
 */
const ERROR_MESSAGES = {
  // Authentication & Authorization
  'UNAUTHORIZED': 'Sesi tamat. Sila log masuk semula.',
  'FORBIDDEN': 'Anda tidak mempunyai kebenaran untuk akses ini.',
  'SESSION_EXPIRED': 'Sesi tamat. Sila log masuk semula.',
  
  // Validation
  'VALIDATION_ERROR': 'Maklumat tidak lengkap atau tidak sah.',
  'MISSING_REQUIRED_FIELD': 'Maklumat wajib tidak lengkap.',
  'INVALID_FORMAT': 'Format data tidak sah.',
  
  // Data Issues
  'NOT_FOUND': 'Rekod tidak dijumpai.',
  'DUPLICATE': 'Rekod sudah wujud dalam sistem.',
  'CONFLICT': 'Operasi tidak dapat dilaksanakan kerana konflik data.',
  
  // Database
  'DATABASE_ERROR': 'Ralat pangkalan data. Sila hubungi pentadbir sistem.',
  'FOREIGN_KEY_VIOLATION': 'Tidak dapat memadam kerana ada rekod lain yang bergantung padanya.',
  'DATABASE_CONFIG_ERROR': 'Ralat konfigurasi sistem.',
  
  // Network
  'NETWORK_ERROR': 'Ralat rangkaian. Sila semak sambungan internet anda.',
  'TIMEOUT': 'Permintaan mengambil masa terlalu lama. Sila cuba lagi.',
  
  // General
  'BAD_REQUEST': 'Permintaan tidak sah.',
  'INTERNAL_ERROR': 'Ralat sistem. Sila hubungi pentadbir sistem.',
  'UNKNOWN': 'Ralat tidak diketahui. Sila cuba lagi.',
};

/**
 * Get user-friendly error message from error response
 * @param {Object} error - Axios error object or regular Error
 * @returns {string} User-friendly error message in Malay
 */
export function getErrorMessage(error) {
  // Check for authentication/session errors thrown before network call
  if (error && error.message) {
    if (error.message.includes('Session expired') || error.message.includes('Please login')) {
      return ERROR_MESSAGES.SESSION_EXPIRED;
    }
    if (error.message.includes('department') || error.message.includes('bahagian')) {
      return ERROR_MESSAGES.SESSION_EXPIRED;
    }
  }

  // Check if error message contains backend error (from fetch API)
  if (error.message && !error.message.includes('Network') && !error.message.includes('Failed to fetch')) {
    // If it's not a generic network error, return the backend error message directly
    return error.message;
  }

  // Network error (no response from server)
  if (!error.response) {
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      return ERROR_MESSAGES.TIMEOUT;
    }
    return ERROR_MESSAGES.NETWORK_ERROR;
  }

  const { status, data } = error.response;

  // Server returned error with code
  if (data?.code && ERROR_MESSAGES[data.code]) {
    return ERROR_MESSAGES[data.code];
  }

  // Server returned error with message
  if (data?.message) {
    return data.message;
  }

  // Fallback based on status code
  switch (status) {
    case 400:
      return ERROR_MESSAGES.BAD_REQUEST;
    case 401:
      return ERROR_MESSAGES.UNAUTHORIZED;
    case 403:
      return ERROR_MESSAGES.FORBIDDEN;
    case 404:
      return ERROR_MESSAGES.NOT_FOUND;
    case 409:
      return ERROR_MESSAGES.DUPLICATE;
    case 500:
      return ERROR_MESSAGES.INTERNAL_ERROR;
    default:
      return ERROR_MESSAGES.UNKNOWN;
  }
}

/**
 * Handle API error and display to user
 * @param {Object} error - Axios error object
 * @param {Function} showError - Function to show error notification (from useSnackbar hook)
 * @param {string} defaultMessage - Optional default message if no specific error found
 * @returns {string} The error message that was displayed
 */
export function handleApiError(error, showError, defaultMessage = null) {
  const message = defaultMessage || getErrorMessage(error);
  
  if (showError) {
    // Call showError directly - it already sets title to "Ralat" and severity to "error"
    showError(message);
  }
  
  // Log error to console in development
  if (process.env.NODE_ENV === 'development') {
    console.error('API Error:', {
      message,
      status: error.response?.status,
      data: error.response?.data,
      originalError: error
    });
  }
  
  return message;
}

/**
 * Check if error is authentication related
 * @param {Object} error - Axios error object
 * @returns {boolean} True if auth error
 */
export function isAuthError(error) {
  const status = error.response?.status;
  const code = error.response?.data?.code;
  
  return status === 401 || 
         code === 'UNAUTHORIZED' || 
         code === 'SESSION_EXPIRED';
}

/**
 * Check if error is permission related
 * @param {Object} error - Axios error object
 * @returns {boolean} True if permission error
 */
export function isPermissionError(error) {
  const status = error.response?.status;
  const code = error.response?.data?.code;
  
  return status === 403 || code === 'FORBIDDEN';
}

/**
 * Check if error is duplicate entry
 * @param {Object} error - Axios error object
 * @returns {boolean} True if duplicate error
 */
export function isDuplicateError(error) {
  const status = error.response?.status;
  const code = error.response?.data?.code;
  
  return status === 409 || code === 'DUPLICATE';
}

/**
 * Check if error is not found
 * @param {Object} error - Axios error object
 * @returns {boolean} True if not found error
 */
export function isNotFoundError(error) {
  const status = error.response?.status;
  const code = error.response?.data?.code;
  
  return status === 404 || code === 'NOT_FOUND';
}

/**
 * Handle error with automatic redirect on auth failure
 * @param {Object} error - Axios error object
 * @param {Function} showSnackbar - Function to show snackbar notification
 * @param {Function} navigate - React router navigate function
 * @param {string} defaultMessage - Optional default message
 */
export function handleApiErrorWithRedirect(error, showSnackbar, navigate, defaultMessage = null) {
  const message = handleApiError(error, showSnackbar, defaultMessage);
  
  // Redirect to login on auth error
  if (isAuthError(error)) {
    setTimeout(() => {
      localStorage.removeItem('user');
      navigate('/authentication/sign-in');
    }, 2000);
  }
  
  return message;
}

/**
 * Extract field-specific validation errors from error response
 * @param {Object} error - Axios error object
 * @returns {Object} Object with field names as keys and error messages as values
 */
export function getFieldErrors(error) {
  const details = error.response?.data?.details;
  
  if (details && typeof details === 'object') {
    return details;
  }
  
  return {};
}

export default {
  getErrorMessage,
  handleApiError,
  handleApiErrorWithRedirect,
  isAuthError,
  isPermissionError,
  isDuplicateError,
  isNotFoundError,
  getFieldErrors,
  ERROR_MESSAGES
};
